import React from "react";
import { Typography } from "@mui/material";
import { Alert } from "@components/Alert/Alert";
import type {
  FinalLoadingReport,
  ReportContactEntry,
  ReportContactRole,
} from "@services/drybulkCargoOperationsApi";
import { useTranslation } from "react-i18next";
import { DRYBULK_CARGO_OPS_LANG_BASE } from "../constants";
import { formatInstant } from "../utils/formatInstant";
import {
  columnLabel,
  formatQuantity,
  formatReportDate,
  formatSurvey,
  formatWater,
  formatWhole,
  sameColumn,
} from "./reportFormat";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.detail.final-report`;
const dash = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === "" ? "-" : String(value);

const TABLE = "min-w-full text-sm border-collapse";
const HEAD = "border-b border-gray-300 px-2 py-1 text-left font-semibold whitespace-nowrap";
const CELL = "border-b border-gray-200 px-2 py-1 whitespace-nowrap";
const NUM = "text-right";
const TOTAL_ROW = "font-semibold bg-gray-50";

const Section: React.FC<{ testId: string; title: string; children: React.ReactNode }> = ({
  testId,
  title,
  children,
}) => (
  <section data-testid={testId} className="flex flex-col gap-2">
    <Typography variant="h6" component="h3">
      {title}
    </Typography>
    {children}
  </section>
);

const Field: React.FC<{
  testId: string;
  label: string;
  value: React.ReactNode;
  muted?: boolean;
}> = ({ testId, label, value, muted = false }) => (
  <div className="flex flex-col gap-0.5 rounded border border-gray-200 px-3 py-2">
    <span className="text-xs text-gray-600">{label}</span>
    <span
      data-testid={testId}
      className={muted ? "italic text-gray-500" : undefined}
      style={{ overflowWrap: "anywhere" }}
    >
      {value}
    </span>
  </div>
);

/** Section 1 - general info; the API's null and the missing-fields list decide the wording, nothing is derived. */
export const GeneralInfoSection: React.FC<{ report: FinalLoadingReport }> = ({ report }) => {
  const { t } = useTranslation();
  const info = report.generalInfo;
  const missing = (field: "WATER_CONSUMPTION" | "MAX_FEED_RATE") =>
    (info.missingGeneralInfo ?? []).includes(field);
  const departure = info.shipDepartureUnavailable
    ? t(`${L}.general.unavailable`)
    : formatInstant(info.shipDeparture);
  return (
    <Section testId="final-report-general" title={t(`${L}.general.title`)}>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
        <Field
          testId="final-report-report-date"
          label={t(`${L}.general.report-date`)}
          value={formatReportDate(info.reportDate)}
        />
        <Field
          testId="final-report-ship-berthed"
          label={t(`${L}.general.ship-berthed`)}
          value={formatInstant(info.shipBerthed)}
        />
        <Field
          testId="final-report-loading-commenced"
          label={t(`${L}.general.loading-commenced`)}
          value={formatInstant(info.loadingCommenced)}
        />
        <Field
          testId="final-report-aggregate-size"
          label={t(`${L}.general.aggregate-size`)}
          value={dash((info.aggregateSizes ?? []).join(", "))}
        />
        <Field
          testId="final-report-via-name"
          label={t(`${L}.general.via-name`)}
          value={info.viaName ? info.viaName : t(`${L}.general.not-defined`)}
          muted={!info.viaName}
        />
        <Field
          testId="final-report-via-number"
          label={t(`${L}.general.via-number`)}
          value={info.viaNumber ? info.viaNumber : t(`${L}.general.not-defined`)}
          muted={!info.viaNumber}
        />
        <Field
          testId="final-report-water-consumption"
          label={t(`${L}.general.water-consumption`)}
          value={
            missing("WATER_CONSUMPTION")
              ? t(`${L}.general.not-entered`)
              : formatWater(info.waterConsumption)
          }
          muted={missing("WATER_CONSUMPTION")}
        />
        <Field
          testId="final-report-loading-completed"
          label={t(`${L}.general.loading-completed`)}
          value={formatInstant(info.loadingCompleted)}
        />
        <Field
          testId="final-report-ship-departure"
          label={t(`${L}.general.ship-departure`)}
          value={departure}
          muted={info.shipDepartureUnavailable}
        />
        <Field
          testId="final-report-max-feed-rate"
          label={t(`${L}.general.max-feed-rate`)}
          value={
            missing("MAX_FEED_RATE") ? t(`${L}.general.not-entered`) : formatWhole(info.maxFeedRate)
          }
          muted={missing("MAX_FEED_RATE")}
        />
        <Field
          testId="final-report-final-belt-scale"
          label={t(`${L}.general.final-belt-scale`)}
          value={formatQuantity(info.finalBeltScale)}
        />
        <Field
          testId="final-report-final-draft-survey"
          label={t(`${L}.general.final-draft-survey`)}
          value={formatSurvey(info.finalDraftSurvey)}
        />
      </div>
    </Section>
  );
};

/** Section 2 - one card per Shipper / S.Pile combination; a missing adjusted total waits for the Final Draft Survey. */
export const CombinationsSection: React.FC<{ report: FinalLoadingReport }> = ({ report }) => {
  const { t } = useTranslation();
  return (
    <Section testId="final-report-combinations" title={t(`${L}.combinations.title`)}>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
        {report.combinations.map((combination) => (
          <div
            key={combination.number}
            data-testid={`final-report-combination-${combination.number}`}
            className="flex flex-col gap-1 rounded border border-gray-200 px-3 py-2 text-sm"
          >
            <strong>{t(`${L}.combinations.number`, { number: combination.number })}</strong>
            <span>
              {t(`${L}.combinations.stock-pile`)}: {combination.stockPileCode}
            </span>
            <span>
              {t(`${L}.combinations.shipper`)}: {combination.shipperCompanyCode}
            </span>
            {combination.adjusted
              .filter((entry) => entry.value !== null)
              .map((entry) => (
                <span key={`${entry.cargoCode}-${entry.gradeSizeCode}`}>
                  {columnLabel(entry)}: {formatQuantity(entry.value)}
                </span>
              ))}
            <strong data-testid={`final-report-combination-${combination.number}-total`}>
              {t(`${L}.combinations.total`)}:{" "}
              {combination.totalAdjusted === null
                ? t(`${L}.combinations.pending-survey`)
                : formatQuantity(combination.totalAdjusted)}
            </strong>
          </div>
        ))}
      </div>
    </Section>
  );
};

/** Section 3 - the Ship loading report: one column per `columns` entry, however many, scrolling sideways when wide (BL-19). */
export const ShipLoadingSection: React.FC<{ report: FinalLoadingReport }> = ({ report }) => {
  const { t } = useTranslation();
  return (
    <Section testId="final-report-ship-loading" title={t(`${L}.loading.title`)}>
      <div className="overflow-x-auto" data-testid="final-report-ship-loading-scroll">
        <table className={TABLE} data-testid="final-report-ship-loading-table">
          <thead>
            <tr>
              {["seq", "hold", "stock-pile", "shipper", "cargo"].map((key) => (
                <th key={key} className={HEAD}>
                  {t(`${L}.loading.columns.${key}`)}
                </th>
              ))}
              {report.columns.map((column) => (
                <th
                  key={columnLabel(column)}
                  className={`${HEAD} ${NUM}`}
                  data-testid="final-report-grade-column"
                >
                  {columnLabel(column)}
                </th>
              ))}
              <th className={HEAD}>{t(`${L}.loading.columns.hopper`)}</th>
              <th className={`${HEAD} ${NUM}`}>{t(`${L}.loading.columns.actual`)}</th>
            </tr>
          </thead>
          <tbody>
            {report.sequences.map((row) => (
              <tr key={row.sequenceNo} data-testid={`final-report-sequence-${row.sequenceNo}`}>
                <td className={CELL}>{row.sequenceNo}</td>
                <td className={CELL}>{row.hold}</td>
                <td className={CELL}>{dash(row.stockPileCode)}</td>
                <td className={CELL}>{dash(row.shipperCompanyCode)}</td>
                <td className={CELL}>{dash(row.cargoCode)}</td>
                {report.columns.map((column) => (
                  <td
                    key={columnLabel(column)}
                    className={`${CELL} ${NUM}`}
                    data-testid={`final-report-sequence-${row.sequenceNo}-${columnLabel(column)}`}
                  >
                    {sameColumn(row, column) ? formatQuantity(row.actual) : ""}
                  </td>
                ))}
                <td className={CELL}>{dash(row.hopperCode)}</td>
                <td className={`${CELL} ${NUM}`}>{formatQuantity(row.actual)}</td>
              </tr>
            ))}
            <tr className={TOTAL_ROW} data-testid="final-report-s-total">
              <td className={CELL} colSpan={5}>
                {t(`${L}.loading.s-total`)}
              </td>
              {report.columns.map((column) => (
                <td key={columnLabel(column)} className={`${CELL} ${NUM}`}>
                  {formatQuantity(
                    report.columnTotals.find((total) => sameColumn(total, column))?.actual,
                  )}
                </td>
              ))}
              <td className={CELL} />
              <td className={`${CELL} ${NUM}`} data-testid="final-report-sequences-total">
                {formatQuantity(report.sequencesTotal)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <table className={`${TABLE} max-w-md`} data-testid="final-report-hopper-totals">
        <thead>
          <tr>
            <th className={HEAD}>{t(`${L}.loading.columns.hopper`)}</th>
            <th className={`${HEAD} ${NUM}`}>{t(`${L}.loading.hopper-total`)}</th>
          </tr>
        </thead>
        <tbody>
          {report.hopperTotals.map((total) => (
            <tr key={total.hopperCode ?? "none"}>
              <td className={CELL}>{dash(total.hopperCode)}</td>
              <td className={`${CELL} ${NUM}`}>{formatQuantity(total.actual)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
};

/** Section 4 - the Loading report per Shipper: planned, actual and the adjusted figure (null until the survey). */
export const ShipperSplitSection: React.FC<{ report: FinalLoadingReport }> = ({ report }) => {
  const { t } = useTranslation();
  return (
    <Section testId="final-report-per-shipper" title={t(`${L}.per-shipper.title`)}>
      <div className="overflow-x-auto">
        <table className={TABLE}>
          <thead>
            <tr>
              {["shipper", "cargo", "size"].map((key) => (
                <th key={key} className={HEAD}>
                  {t(`${L}.per-shipper.columns.${key}`)}
                </th>
              ))}
              {["planned", "actual", "adjusted"].map((key) => (
                <th key={key} className={`${HEAD} ${NUM}`}>
                  {t(`${L}.per-shipper.columns.${key}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {report.shipperSplit.map((split) => (
              <tr key={`${split.shipperCompanyCode}-${columnLabel(split)}`}>
                <td className={CELL}>{split.shipperCompanyCode}</td>
                <td className={CELL}>{split.cargoCode}</td>
                <td className={CELL}>{split.gradeSizeCode}</td>
                <td className={`${CELL} ${NUM}`}>{formatQuantity(split.planned)}</td>
                <td className={`${CELL} ${NUM}`}>{formatQuantity(split.actual)}</td>
                <td className={`${CELL} ${NUM}`}>{formatQuantity(split.adjusted)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
};

/** Section 5 - the stowage plan: a column per hold that has data (Holds 7-9 included), a row per grade column. */
export const StowageSection: React.FC<{ report: FinalLoadingReport }> = ({ report }) => {
  const { t } = useTranslation();
  const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);
  return (
    <Section testId="final-report-stowage" title={t(`${L}.stowage.title`)}>
      <div className="overflow-x-auto" data-testid="final-report-stowage-scroll">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={HEAD} />
              {report.holds.map((hold) => (
                <th
                  key={hold.hold}
                  className={`${HEAD} ${NUM}`}
                  data-testid={`final-report-stowage-hold-${hold.hold}`}
                >
                  {t(`${L}.stowage.hold`, { hold: hold.hold })}
                </th>
              ))}
              <th className={`${HEAD} ${NUM}`}>{t(`${L}.stowage.total`)}</th>
            </tr>
          </thead>
          <tbody>
            {report.columns.map((column) => {
              const cells = report.holds.map(
                (hold) => hold.byColumn.find((entry) => sameColumn(entry, column))?.actual,
              );
              return (
                <tr key={columnLabel(column)}>
                  <td className={CELL}>{columnLabel(column)}</td>
                  {cells.map((value, index) => (
                    <td key={report.holds[index].hold} className={`${CELL} ${NUM}`}>
                      {value === undefined ? "" : formatQuantity(value)}
                    </td>
                  ))}
                  <td className={`${CELL} ${NUM}`}>
                    {formatQuantity(
                      sum(cells.filter((value): value is number => value !== undefined)),
                    )}
                  </td>
                </tr>
              );
            })}
            <tr className={TOTAL_ROW} data-testid="final-report-stowage-actual">
              <td className={CELL}>{t(`${L}.stowage.actual`)}</td>
              {report.holds.map((hold) => (
                <td key={hold.hold} className={`${CELL} ${NUM}`}>
                  {formatQuantity(hold.actual)}
                </td>
              ))}
              <td className={`${CELL} ${NUM}`}>
                {formatQuantity(sum(report.holds.map((hold) => hold.actual)))}
              </td>
            </tr>
            <tr data-testid="final-report-stowage-planned">
              <td className={CELL}>{t(`${L}.stowage.planned`)}</td>
              {report.holds.map((hold) => (
                <td key={hold.hold} className={`${CELL} ${NUM}`}>
                  {formatQuantity(hold.planned)}
                </td>
              ))}
              <td className={`${CELL} ${NUM}`}>
                {formatQuantity(sum(report.holds.map((hold) => hold.planned)))}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </Section>
  );
};

const ROLE_KEY: Record<ReportContactRole, string> = {
  SHIPPER: "shipper",
  STOCK_PILE: "stock-pile",
  TRANSPORTER: "transporter",
  AGENCY: "agency",
};

const ContactRow: React.FC<{ contact: ReportContactEntry; index: number }> = ({
  contact,
  index,
}) => {
  const { t } = useTranslation();
  const label = (
    <span className="italic text-gray-500" data-testid={`final-report-contact-${index}-state`}>
      {contact.state === "NO_LICENCE"
        ? t(`${L}.contacts.no-licence`)
        : t(`${L}.contacts.unavailable`)}
    </span>
  );
  const unusable =
    contact.state === "NO_LICENCE" ||
    contact.state === "UNAVAILABLE" ||
    contact.state === "PENDING" ||
    contact.state === null;
  return (
    <tr data-testid={`final-report-contact-${index}`}>
      <td className={CELL}>{t(`${L}.contacts.roles.${ROLE_KEY[contact.role]}`)}</td>
      <td className={CELL}>{contact.companyCode}</td>
      <td className={CELL}>{unusable ? label : dash(contact.applicantName)}</td>
      <td className={CELL}>
        {unusable ? "" : dash(contact.mobileNumber ?? contact.telephoneNumber)}
      </td>
      <td className={CELL}>
        {unusable ? "" : dash(contact.applicantEmail ?? contact.companyEmail)}
      </td>
    </tr>
  );
};

/** Section 6 - contacts; an unavailable party is a visible label, never an empty cell (BL-18). */
export const ContactsSection: React.FC<{ report: FinalLoadingReport }> = ({ report }) => {
  const { t } = useTranslation();
  return (
    <Section testId="final-report-contacts" title={t(`${L}.contacts.title`)}>
      {report.contactsUnavailable && <Alert type="warning">{t(`${L}.contacts.banner`)}</Alert>}
      <div className="overflow-x-auto">
        <table className={TABLE}>
          <thead>
            <tr>
              {["role", "code", "name", "phone", "email"].map((key) => (
                <th key={key} className={HEAD}>
                  {t(`${L}.contacts.columns.${key}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {report.contacts.map((contact, index) => (
              <ContactRow
                key={`${contact.role}-${contact.companyCode}-${index}`}
                contact={contact}
                index={index}
              />
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
};
